import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquarePlus, Send, Bug, Lightbulb, MessageCircle, Check,
  ChevronUp, User, Loader2, TrendingUp, Clock, Flame, CheckCircle2, Circle
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish, getRelays } from '../hooks/useNostrPublish';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { timeAgo } from '../lib/utils';

// ── Config ──────────────────────────────────────────────────────────
// Your pubkey — feedback gets p-tagged to you so you see it in notifications
const ADMIN_PUBKEY = 'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc';
const FEEDBACK_TAG = 'bchnostr-feedback';
// Reaction content that marks an item as addressed/completed
const DONE_MARKER = '✅';

type Category = 'suggestion' | 'bug' | 'general';
type SortMode = 'popular' | 'recent';
type StatusTab = 'open' | 'completed';

const CATEGORIES: { id: Category; label: string; icon: React.ReactNode; placeholder: string }[] = [
  {
    id: 'suggestion',
    label: 'Feature Request',
    icon: <Lightbulb className="w-4 h-4" />,
    placeholder: 'I wish BCHNostr could...',
  },
  {
    id: 'bug',
    label: 'Bug Report',
    icon: <Bug className="w-4 h-4" />,
    placeholder: 'Something broke — here\'s what happened...',
  },
  {
    id: 'general',
    label: 'General Feedback',
    icon: <MessageCircle className="w-4 h-4" />,
    placeholder: 'Here\'s what I think about BCHNostr...',
  },
];

// ── Main Page ──────────────────────────────────────────────────────

export default function FeedbackPage() {
  const { user } = useAppContext();

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <MessageSquarePlus className="w-5 h-5 text-primary" /> Suggestion Box
        </h1>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Intro */}
        <div className="text-center py-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <MessageSquarePlus className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-base font-bold">Help us build a better BCHNostr</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Got an idea, found a bug, or just want to share your thoughts?
            Drop it here and upvote the suggestions you want to see built first.
          </p>
        </div>

        {/* Submit form */}
        {user ? (
          <FeedbackForm />
        ) : (
          <div className="rounded-xl border border-border bg-card p-6 text-center space-y-3">
            <MessageCircle className="w-8 h-8 text-muted-foreground/30 mx-auto" />
            <p className="text-sm text-muted-foreground">Log in to submit feedback</p>
            <p className="text-xs text-muted-foreground/60">
              Your feedback is published as a Nostr note so we can reply to you directly.
            </p>
          </div>
        )}

        {/* Feedback feed with voting */}
        <FeedbackFeed />
      </div>
    </div>
  );
}

// ── Submit Form ────────────────────────────────────────────────────

function FeedbackForm() {
  const { publish } = useNostrPublish();
  const [category, setCategory] = useState<Category>('suggestion');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const cat = CATEGORIES.find(c => c.id === category)!;

  const handleSubmit = async () => {
    if (!content.trim() || sending) return;
    setSending(true);

    try {
      const tags: string[][] = [
        ['t', FEEDBACK_TAG],
        ['t', category],
        ['p', ADMIN_PUBKEY], // notify you
      ];

      // Prefix content with category label for easy scanning
      const prefix = category === 'bug' ? '[Bug] '
        : category === 'suggestion' ? '[Feature Request] '
        : '[Feedback] ';

      await publish({
        kind: 1,
        content: prefix + content.trim(),
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      setSent(true);
      setContent('');
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error('Failed to publish feedback:', err);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center space-y-2">
        <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
          <Check className="w-6 h-6 text-emerald-500" />
        </div>
        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Thanks for your feedback!</p>
        <p className="text-xs text-muted-foreground">
          It's been published to Nostr. We'll review it and may reply directly to your note.
        </p>
        <button
          onClick={() => setSent(false)}
          className="text-xs text-primary font-medium mt-2"
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Category picker */}
      <div className="flex border-b border-border">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            onClick={() => { setCategory(c.id); textareaRef.current?.focus(); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              category === c.id
                ? 'bg-primary/5 text-primary border-b-2 border-primary -mb-px'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {c.icon}
            <span className="hidden sm:inline">{c.label}</span>
            <span className="sm:hidden">
              {c.id === 'suggestion' ? 'Idea' : c.id === 'bug' ? 'Bug' : 'Other'}
            </span>
          </button>
        ))}
      </div>

      {/* Text input */}
      <div className="p-4 space-y-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder={cat.placeholder}
          className="w-full bg-transparent text-sm placeholder:text-muted-foreground/50 focus:outline-none resize-none min-h-[120px]"
          rows={5}
        />

        <div className="flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground/50">
            Published as a Nostr note tagged #{FEEDBACK_TAG}
          </p>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || sending}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Feedback Feed with Voting ───────────────────────────────────────

function FeedbackFeed() {
  const { user } = useAppContext();
  const [sortMode, setSortMode] = useState<SortMode>('popular');
  const [statusTab, setStatusTab] = useState<StatusTab>('open');

  const isAdmin = !!user && user.pubkey === ADMIN_PUBKEY;

  // Fetch all feedback posts
  const { events: feedbackEvents, loading } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [FEEDBACK_TAG], limit: 50 }],
    allRelays: true,
  });

  // Collect all feedback event IDs for batch reaction query
  const eventIds = useMemo(
    () => feedbackEvents.map(e => e.id),
    [feedbackEvents.map(e => e.id).join(',')]
  );

  // Batch-fetch all reactions (kind 7) for all feedback posts at once
  // This includes both upvotes AND admin done-markers
  const { events: allReactions, refetch: refetchReactions } = useNostrQuery({
    filters: eventIds.length > 0 ? [{ kinds: [7], '#e': eventIds, limit: 2000 }] : [],
    enabled: eventIds.length > 0,
    allRelays: true,
  });

  // Build a vote-count map: eventId -> { count, voters Set }
  // AND a done-set: event IDs marked as completed by admin
  //
  // For done status, we track the LATEST admin reaction per feedback item.
  // ✅ = done, 🔄 = reopened. The most recent one wins.
  const { voteMap, doneSet } = useMemo(() => {
    const map = new Map<string, { count: number; voters: Set<string> }>();
    const done = new Set<string>();

    // Track latest admin status reaction per feedback event
    // key = feedback event ID, value = { isDone, timestamp }
    const adminStatus = new Map<string, { isDone: boolean; at: number }>();

    // Init all feedback events
    for (const ev of feedbackEvents) {
      map.set(ev.id, { count: 0, voters: new Set() });
    }

    for (const reaction of allReactions) {
      const eTags = reaction.tags.filter((t: string[]) => t[0] === 'e');

      // Check if this is an admin status reaction (done or reopen)
      if (reaction.pubkey === ADMIN_PUBKEY && (reaction.content === DONE_MARKER || reaction.content === '🔄')) {
        for (const eTag of eTags) {
          const targetId = eTag[1];
          const existing = adminStatus.get(targetId);
          // Only update if this reaction is newer
          if (!existing || reaction.created_at > existing.at) {
            adminStatus.set(targetId, {
              isDone: reaction.content === DONE_MARKER,
              at: reaction.created_at,
            });
          }
        }
        continue; // Don't count status markers as upvotes
      }

      // Count as upvote (skip downvotes)
      if (reaction.content === '-') continue;
      for (const eTag of eTags) {
        const targetId = eTag[1];
        const entry = map.get(targetId);
        if (entry && !entry.voters.has(reaction.pubkey)) {
          entry.voters.add(reaction.pubkey);
          entry.count++;
        }
      }
    }

    // Build final done set from latest admin status
    for (const [eventId, status] of adminStatus) {
      if (status.isDone) done.add(eventId);
    }

    return { voteMap: map, doneSet: done };
  }, [feedbackEvents, allReactions]);

  // Track locally-toggled done IDs for instant UI feedback before relay propagation
  const [localDone, setLocalDone] = useState<Set<string>>(new Set());
  const [localUndone, setLocalUndone] = useState<Set<string>>(new Set());

  const isDone = useCallback((eventId: string) => {
    if (localUndone.has(eventId)) return false;
    return doneSet.has(eventId) || localDone.has(eventId);
  }, [doneSet, localDone, localUndone]);

  // Counts for tabs
  const counts = useMemo(() => {
    let open = 0;
    let completed = 0;
    for (const ev of feedbackEvents) {
      if (isDone(ev.id)) completed++;
      else open++;
    }
    return { open, completed };
  }, [feedbackEvents, isDone]);

  // Filter + sort
  const sorted = useMemo(() => {
    const items = feedbackEvents.filter(ev =>
      statusTab === 'completed' ? isDone(ev.id) : !isDone(ev.id)
    );

    if (sortMode === 'popular') {
      items.sort((a, b) => {
        const voteDiff = (voteMap.get(b.id)?.count || 0) - (voteMap.get(a.id)?.count || 0);
        return voteDiff !== 0 ? voteDiff : b.created_at - a.created_at;
      });
    } else {
      items.sort((a, b) => b.created_at - a.created_at);
    }
    return items;
  }, [feedbackEvents, voteMap, sortMode, statusTab, isDone]);

  if (loading && sorted.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-muted-foreground/30 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Status tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-border">
        <button
          onClick={() => setStatusTab('open')}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px ${
            statusTab === 'open'
              ? 'text-foreground border-primary'
              : 'text-muted-foreground border-transparent hover:text-foreground'
          }`}
        >
          <Circle className="w-3.5 h-3.5" />
          Open
          {counts.open > 0 && (
            <span className="text-[10px] font-bold bg-muted px-1.5 py-0.5 rounded-full tabular-nums">{counts.open}</span>
          )}
        </button>
        <button
          onClick={() => setStatusTab('completed')}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px ${
            statusTab === 'completed'
              ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500'
              : 'text-muted-foreground border-transparent hover:text-foreground'
          }`}
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          Completed
          {counts.completed > 0 && (
            <span className="text-[10px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full tabular-nums">{counts.completed}</span>
          )}
        </button>

        <div className="flex-1" />

        {/* Sort toggle */}
        <div className="flex bg-muted/50 rounded-lg p-0.5 mb-1">
          <button
            onClick={() => setSortMode('popular')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
              sortMode === 'popular'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Flame className="w-3 h-3" />
            Popular
          </button>
          <button
            onClick={() => setSortMode('recent')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
              sortMode === 'recent'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock className="w-3 h-3" />
            Recent
          </button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-12 px-4">
          {statusTab === 'completed' ? (
            <>
              <CheckCircle2 className="w-8 h-8 text-muted-foreground/15 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No completed items yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Addressed suggestions and fixed bugs will appear here.</p>
            </>
          ) : (
            <>
              <MessageCircle className="w-8 h-8 text-muted-foreground/15 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No open feedback</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {counts.completed > 0
                  ? 'Everything has been addressed! Submit new feedback above.'
                  : 'Be the first to share your thoughts!'}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((event, index) => (
            <FeedbackItem
              key={event.id}
              event={event}
              voteCount={voteMap.get(event.id)?.count || 0}
              voters={voteMap.get(event.id)?.voters || new Set()}
              rank={sortMode === 'popular' && statusTab === 'open' ? index + 1 : undefined}
              done={isDone(event.id)}
              isAdmin={isAdmin}
              onToggleDone={async (eventId, markDone) => {
                // Optimistic update
                if (markDone) {
                  setLocalDone(prev => new Set(prev).add(eventId));
                  setLocalUndone(prev => { const s = new Set(prev); s.delete(eventId); return s; });
                } else {
                  setLocalUndone(prev => new Set(prev).add(eventId));
                  setLocalDone(prev => { const s = new Set(prev); s.delete(eventId); return s; });
                }
                // Refetch after a delay to pick up the published event
                setTimeout(() => refetchReactions(), 2000);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Feedback Item with Upvote + Done Status ─────────────────────────

function FeedbackItem({ event, voteCount, voters, rank, done, isAdmin, onToggleDone }: {
  event: any;
  voteCount: number;
  voters: Set<string>;
  rank?: number;
  done: boolean;
  isAdmin: boolean;
  onToggleDone: (eventId: string, markDone: boolean) => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(event.pubkey);
  const [localVoted, setLocalVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const name = profile?.display_name || profile?.name || event.pubkey.slice(0, 8) + '...';
  const isAdminPost = event.pubkey === ADMIN_PUBKEY;

  // Check if current user already voted
  const alreadyVoted = localVoted || (user ? voters.has(user.pubkey) : false);
  const displayCount = voteCount + (localVoted ? 1 : 0);

  const handleUpvote = async () => {
    if (!user || alreadyVoted || voting) return;
    setVoting(true);
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
          ['k', '1'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setLocalVoted(true);
    } catch (err) {
      console.error('Failed to upvote:', err);
    } finally {
      setVoting(false);
    }
  };

  const handleToggleDone = async () => {
    if (!isAdmin || toggling) return;
    setToggling(true);
    try {
      if (!done) {
        // Mark as done — publish a ✅ reaction from admin
        await publish({
          kind: 7,
          content: DONE_MARKER,
          tags: [
            ['e', event.id],
            ['p', event.pubkey],
            ['k', '1'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
      } else {
        // Undo: publish a kind 5 deletion for the done-marker reaction
        // For simplicity, just publish a "-done" reaction to override
        // (We'll treat the latest admin reaction as the status)
        // Actually, just re-mark as open by publishing a "reopen" reaction
        await publish({
          kind: 7,
          content: '🔄',
          tags: [
            ['e', event.id],
            ['p', event.pubkey],
            ['k', '1'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
      }
      onToggleDone(event.id, !done);
    } catch (err) {
      console.error('Failed to toggle done status:', err);
    } finally {
      setToggling(false);
    }
  };

  // Detect category from content prefix or tags
  const hasBugTag = event.tags?.some((t: string[]) => t[0] === 't' && t[1] === 'bug');
  const hasSuggestionTag = event.tags?.some((t: string[]) => t[0] === 't' && t[1] === 'suggestion');
  const isBug = hasBugTag || event.content.startsWith('[Bug]');
  const isSuggestion = hasSuggestionTag || event.content.startsWith('[Feature Request]');

  // Strip the prefix for display
  const displayContent = event.content
    .replace(/^\[(Bug|Feature Request|Feedback)\]\s*/i, '')
    .trim();

  const categoryBadge = isBug
    ? { label: 'Bug', color: 'bg-red-500/10 text-red-500 border-red-500/20', icon: <Bug className="w-3 h-3" /> }
    : isSuggestion
      ? { label: 'Idea', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', icon: <Lightbulb className="w-3 h-3" /> }
      : { label: 'Feedback', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20', icon: <MessageCircle className="w-3 h-3" /> };

  return (
    <div className={`rounded-xl border bg-card overflow-hidden transition-all ${
      done
        ? 'border-emerald-500/20 bg-emerald-500/3'
        : isAdminPost
          ? 'border-primary/30 bg-primary/5'
          : 'border-border'
    } ${rank === 1 && displayCount > 0 ? 'ring-2 ring-amber-500/20' : ''}`}>
      <div className="flex">
        {/* Upvote column */}
        <div className="flex flex-col items-center justify-start py-3 px-3 border-r border-border/50 min-w-[56px]">
          <button
            onClick={handleUpvote}
            disabled={!user || alreadyVoted || voting}
            className={`flex flex-col items-center gap-0.5 transition-all group ${
              alreadyVoted
                ? 'text-primary'
                : 'text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed'
            }`}
            title={!user ? 'Log in to vote' : alreadyVoted ? 'Already voted' : 'Upvote this'}
          >
            {voting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <ChevronUp className={`w-5 h-5 transition-transform ${
                !alreadyVoted && user ? 'group-hover:-translate-y-0.5' : ''
              } ${alreadyVoted ? 'text-primary' : ''}`} />
            )}
            <span className={`text-sm font-bold tabular-nums ${
              alreadyVoted ? 'text-primary' : displayCount > 0 ? 'text-foreground' : 'text-muted-foreground'
            }`}>
              {displayCount}
            </span>
          </button>
          {rank === 1 && displayCount > 0 && (
            <div className="mt-1">
              <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-3 space-y-2 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <Link to={`/profile/${event.pubkey}`} className="flex items-center gap-2 min-w-0">
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <User className="w-3 h-3 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate flex items-center gap-1">
                  {name}
                  {isAdminPost && <span className="text-[9px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">Team</span>}
                </p>
              </div>
            </Link>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[10px] text-muted-foreground">{timeAgo(event.created_at)}</span>
              <span className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${categoryBadge.color}`}>
                {categoryBadge.icon}
                {categoryBadge.label}
              </span>
            </div>
          </div>

          <p className={`text-sm leading-relaxed ${done ? 'text-muted-foreground' : ''}`}>{displayContent}</p>

          {/* Done badge + admin toggle */}
          <div className="flex items-center gap-2">
            {done && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                <CheckCircle2 className="w-3 h-3" />
                Addressed
              </span>
            )}

            {isAdmin && (
              <button
                onClick={handleToggleDone}
                disabled={toggling}
                className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                  done
                    ? 'text-muted-foreground border-border hover:text-foreground hover:bg-muted'
                    : 'text-emerald-600 dark:text-emerald-400 border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10'
                } disabled:opacity-50`}
              >
                {toggling ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : done ? (
                  <><Circle className="w-3 h-3" /> Reopen</>
                ) : (
                  <><CheckCircle2 className="w-3 h-3" /> Mark Done</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

